import { Component, ElementRef, Inject, ViewChild, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Collection } from 'src/app/models/collection.interface';
import { User } from 'src/app/models/user.interface';
import { MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { CognitoService } from 'src/app/services/cognito.service';

@Component({
  selector: 'app-edit-privacy-dialog',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatOptionModule,
    MatAutocompleteModule,
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './edit-privacy-dialog.component.html',
  styleUrl: './edit-privacy-dialog.component.scss'
})
export class EditPrivacyDialogComponent implements OnInit {
  privacyForm!: FormGroup;
  collection: Collection;
  newCollectionName = '';
  users: User[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  private filteredUsersSubject = new BehaviorSubject<{ user_id: string; email: string }[]>([]);
  // For access list dropdown
  filteredUsers$ = this.filteredUsersSubject.asObservable();
  searchControl = new FormControl();
  authenticated_user_id = '';
  @ViewChild('chipInput') chipInput!: ElementRef<HTMLInputElement>;

  constructor(
    public dialogRef: MatDialogRef<EditPrivacyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditPrivacyDialogComponent,
    private fb: FormBuilder,
    private cognitoService: CognitoService
  ) {
    this.collection = data.collection;
    this.privacyForm = this.fb.group({
      is_private: new FormControl(this.collection.is_private),
      access_list: this.fb.array(this.collection.access_list.map(id => this.fb.control(id)))
    });
    this.users = data.users;
  }

  ngOnInit(): void {
    this.cognitoService
      .getUserId()
      .then(userId => {
        this.authenticated_user_id = userId;
      })
      .catch(error => {
        console.error('Error retrieving user id:', error);
      });
    this.searchControl.valueChanges.subscribe(value => {
      this.filteredUsersSubject.next(this._filterUsers(value));
    });
  }

  get isOwner(): boolean {
    return this.collection.owner_id === this.authenticated_user_id;
  }

  displayUser(user: { user_id: string; email: string }): string {
    return user ? user.email : '';
  }

  addAccessFromInput(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;
    const value = event.value.trim();

    if (value) {
      const user = this.users.find(u => u.email.toLowerCase() === value.toLowerCase());
      if (user && !this.accessList.value.includes(user.user_id)) {
        this.accessList.push(new FormControl(user.user_id));
      }
    }

    // Clear the input field and reset the search control
    if (input) {
      input.value = '';
    }
    this.searchControl.setValue(null);
  }

  togglePrivacy(): void {
    this.privacyForm.setValue({ is_private: !this.isPrivate, access_list: this.accessList.value });
  }
  get isPrivate(): boolean {
    return this.privacyForm.get('is_private')?.value;
  }
  get accessList() {
    return this.privacyForm.get('access_list') as FormArray;
  }

  private _filterUsers(value: string): { user_id: string; email: string }[] {
    // Ensure the value is a string
    const filterValue = value ? value.toString().toLowerCase() : '';
    return this.users.filter(user => user.email.toLowerCase().includes(filterValue));
  }

  addAccess(event: MatAutocompleteSelectedEvent): void {
    const user: User = event.option.value;
    if (!this.accessList.value.includes(user.user_id)) {
      this.accessList.push(new FormControl(user.user_id));
    }

    this.searchControl.setValue('');
    if (this.chipInput) {
      this.chipInput.nativeElement.value = '';
    }
  }

  removeAccess(user: string): void {
    const index = this.accessList.controls.findIndex(control => control.value === user);
    if (index >= 0) {
      this.accessList.removeAt(index);
    }
  }

  saveForm(): void {
    if (
      // Privacy hasn't changed
      this.isPrivate === this.collection.is_private &&
      // No users added/moved from access list
      this.accessList.value.length === this.collection.access_list.length &&
      // All users in access list are still in the collection's access list
      this.accessList.value.every((id: string) => this.collection.access_list.includes(id))
    ) {
      this.dialogRef.close();
      return;
    }
    const updatedCollection: Collection = {
      ...this.collection,
      is_private: this.isPrivate,
      access_list: this.accessList.value
    };

    this.dialogRef.close(updatedCollection);
  }

  getEmail(user_id: string): string | undefined {
    const email = this.users.find(user => user.user_id === user_id)?.email;
    if (!email) {
      return '(Unknown)';
    }
    return email;
  }
}
